<template>
    <div class="form-container">
        <h2 style="margin: 0 0 16px 0; color: var(--color-ds-primary);">Geocode Data</h2>
        <div class="form-section">
            <h3>Model Data</h3>
            <div class="form-group">
                <label>Model Name</label>
                <select v-model="localSelectedModel">
                    <option value="">Select Model</option>
                    <option v-for="model in props.models" :key="model.id" :value="model">
                        {{ model.name }}
                    </option>
                </select>
            </div>
            <div class="form-group">
                <label>Table Name</label>
                <select v-model="selectedTable">
                    <option value="">Select Table</option>
                    <option v-for="table in tables" :key="table.name" :value="table.name">
                        {{ table.name }}
                    </option>
                </select>
            </div>
            <button class="button button-full" @click="geocodeTable">Geocode Model Data</button>
        </div>

        <div class="form-section">
            <h3>Worksheet Data</h3>
            <div class="form-group">
                <label>Range Address</label>
                <div style="display: flex; gap: 8px;">
                    <input id="geocode-range" type="text" placeholder="e.g. A1:H10" style="flex: 1;" />
                    <button class="button-small" @click="selectRange">Select</button>
                </div>
            </div>
            <button class="button button-full" @click="geocodeSheet">Geocode Sheet Data</button>
        </div>
    </div>
</template>

<script setup>
/// <reference types="office-js" />

import { defineProps, ref, computed } from 'vue';
import { geocodeLocationData, geocodeModelTable } from '../services/optilogicService';
import { showMessage } from '../utils/messages';
import { getConfigValue } from '../utils/excelUtils';
import { useTableSelection } from '../composables/useTableSelection';
import {
    validateGeocodeHeaders,
    buildGeocodeData,
    parseGeocodeResultId,
    calculateAbsoluteCoordinates
} from '../utils/geocodeHelpers';

const props = defineProps({
    selectedModel: Object,
    models: []
});

// Local model selection
const localSelectedModel = ref(null);

// Create a computed prop object that matches the expected structure
const localPropsForComposable = computed(() => ({
    selectedModel: localSelectedModel.value,
    models: props.models
}));

// Use composable for table selection logic
const { selectedTable, tables } = useTableSelection(localPropsForComposable);

const selectRange = async () => {
    await window.Excel.run(async (context) => {
        try {
            // Launch Excel's built-in range picker
            const range = await context.workbook.getSelectedRange();
            range.load("address");
            await context.sync();

            // Put the selected range into the input box
            const rangeInput = document.getElementById("geocode-range");
            if (rangeInput) {
                rangeInput.value = range.address;
            }
        } catch (error) {
            console.error("Error selecting range:", error);
        }
    });
};

const geocodeSheet = async () => {

    const rangeInput = document.getElementById("geocode-range");// as HTMLInputElement;
    const rangeAddress = rangeInput?.value?.trim();

    if (!rangeAddress) {
        showMessage("Please select a range first.");
        return;
    }

    try {
        await window.Excel.run(async (context) => {
            const workbook = context.workbook;
            const sheet = workbook.worksheets.getActiveWorksheet();
            const range = sheet.getRange(rangeAddress);

            range.load("values, rowIndex, columnIndex");
            await context.sync();

            const startRow = range.rowIndex;
            const startCol = range.columnIndex;

            const values = range.values;

            // Validate headers using helper
            const validation = validateGeocodeHeaders(values);
            if (!validation.valid) {
                showMessage(validation.error);
                return;
            }

            const { headers, zipHeader, latitudeIndex, longitudeIndex } = validation;

            console.log("Latitude column index:", latitudeIndex);
            console.log("Longitude column index:", longitudeIndex);

            const geoprovider = await getConfigValue('geocode provider');
            const geokey = await getConfigValue('geocode key');

            // Build geocode data using helper
            const addressData = buildGeocodeData(values, headers, zipHeader, latitudeIndex, longitudeIndex);

            console.log("Prepared geocode data:", addressData);
            showMessage(`Prepared ${addressData.length} addresses for geocoding`);

            // TODO: Send addressData to your API endpoint
            // await fetch("/api/geocode", { method: "POST", body: JSON.stringify(addressData) });
            const response = await geocodeLocationData(addressData, geoprovider, geokey);

            // Process geocode results
            for (const rowResults of response) {
                if (!rowResults || rowResults.length === 0) continue;

                // Always take the first/highest-confidence match
                const result = rowResults[0];

                // Parse the ID to get offsets
                const offsets = parseGeocodeResultId(result.id);

                // Calculate absolute coordinates
                const coords = calculateAbsoluteCoordinates(startRow, startCol, offsets);

                // Get target cells
                const activeSheet = context.workbook.worksheets.getActiveWorksheet();
                const latCell = activeSheet.getCell(coords.absoluteRow, coords.absoluteLatCol);
                const lonCell = activeSheet.getCell(coords.absoluteRow, coords.absoluteLonCol);

                // Assign geocoded values
                latCell.values = [[result.latitude]];
                lonCell.values = [[result.longitude]];
            }

            await context.sync();

        });
    } catch (err) {
        console.error("Error processing geocode sheet:", err);
        showMessage("Error reading data from Excel range.");
    }
};

const geocodeTable = async () => {

    const geoprovider = await getConfigValue('geocode provider');
    const geokey = await getConfigValue('geocode key');
    const model_name = localSelectedModel.value?.name;
    const table_name = selectedTable.value;

    const r = await geocodeModelTable(model_name, table_name, geoprovider, geokey);

    showMessage(r);

};
</script>
